import { useState } from 'react';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { MapPin, Phone, Mail, Clock, Instagram, Facebook, MessageCircle, Send, CheckCircle, CreditCard, QrCode, Building } from 'lucide-react';
import { toast } from 'sonner';

const contactInfo = [
  {
    icon: Phone,
    title: 'Phone',
    value: '+977-9824042067',
    action: 'tel:+9779824042067',
    actionLabel: 'Call Now',
  },
  {
    icon: Mail,
    title: 'Email',
    value: 'Nutrafuelsupplements@gmail.com',
    action: 'mailto:Nutrafuelsupplements@gmail.com',
    actionLabel: 'Send Email',
  },
  {
    icon: MapPin,
    title: 'Location',
    value: 'Panitanki (Indo-Nepal Border)',
    action: '/stores',
    actionLabel: 'View Store',
  },
  {
    icon: Clock,
    title: 'Business Hours',
    value: 'Mon-Sun: 9AM - 9PM',
    action: null,
    actionLabel: null,
  },
];

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    await new Promise((resolve) => setTimeout(resolve, 1500));

    setIsSubmitting(false);
    setIsSubmitted(true);
    toast.success('Message sent successfully! We\'ll get back to you soon.');

    // Reset form after a delay
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: '', phone: '', email: '', subject: '', message: '' });
    }, 3000);
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-32 md:pt-40 pb-16 bg-gradient-to-b from-muted/50 to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block text-gold font-semibold text-sm uppercase tracking-wider mb-4">GET IN TOUCH</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-foreground leading-tight mb-6">
              Contact &
              <span className="block text-primary">Order</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Have questions? Need product guidance? Our team is ready to help you choose the right nutrition for your goals.
            </p>
          </div>
        </div>
      </section>

      {/* WhatsApp Priority CTA */}
      <section className="py-8 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto p-6 md:p-8 rounded-2xl bg-gradient-to-r from-[#25D366]/10 via-[#25D366]/5 to-[#25D366]/10 border border-[#25D366]/30">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-[#25D366] flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-8 h-8 fill-current text-primary-foreground">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-foreground">Fastest Way to Order</h3>
                  <p className="text-muted-foreground">Get instant response on WhatsApp</p>
                </div>
              </div>
              <Button variant="whatsapp" size="xl" asChild>
                <a href="https://wa.me/9779824042067?text=Hi%20NutraFuel!%20I'm%20interested%20in%20your%20supplements." target="_blank" rel="noopener noreferrer">
                  Order on WhatsApp
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="section-title text-foreground mb-8">Get in Touch</h2>
              <div className="space-y-6 mb-12">
                {contactInfo.map((item) => (
                  <div key={item.title} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground">{item.title}</h4>
                      <p className="text-muted-foreground mb-1">{item.value}</p>
                      {item.action && (
                        <a
                          href={item.action}
                          className="text-sm text-primary hover:text-gold transition-colors"
                        >
                          {item.actionLabel} →
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Social Links */}
              <div className="mb-8">
                <h4 className="font-semibold text-foreground mb-4">Follow Us</h4>
                <div className="flex gap-3">
                  <a
                    href="https://www.instagram.com/nutrafuel_supplements"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-xl bg-muted hover:bg-primary hover:text-primary-foreground flex items-center justify-center transition-colors"
                  >
                    <Instagram className="w-5 h-5" />
                  </a>
                  <a
                    href="https://facebook.com/nutrafuel"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-xl bg-muted hover:bg-primary hover:text-primary-foreground flex items-center justify-center transition-colors"
                  >
                    <Facebook className="w-5 h-5" />
                  </a>
                  <a
                    href="https://wa.me/9779824042067"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-xl bg-muted hover:bg-[#25D366] hover:text-primary-foreground flex items-center justify-center transition-colors"
                  >
                    <MessageCircle className="w-5 h-5" />
                  </a>
                </div>
              </div>

              {/* Payment Methods */}
              <div className="p-6 rounded-2xl bg-gradient-to-r from-green-500/10 to-green-400/10 border border-green-500/20">
                <h4 className="font-bold text-foreground mb-4 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-green-500" />
                  Payment Methods
                </h4>
                <p className="text-sm text-muted-foreground mb-4">
                  We only accept <span className="font-semibold text-foreground">prepaid orders</span>. Payment can be made via:
                </p>
                <div className="flex flex-wrap gap-3">
                  <span className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-600 font-medium rounded-full text-sm">
                    <QrCode className="w-4 h-4" />
                    QR Payment
                  </span>
                  <span className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-600 font-medium rounded-full text-sm">
                    <Building className="w-4 h-4" />
                    Bank Transfer
                  </span>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <Card className="border-0 shadow-elevated">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-foreground mb-6">Send us a Message</h3>
                {isSubmitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-success/20 flex items-center justify-center">
                      <CheckCircle className="w-8 h-8 text-success" />
                    </div>
                    <h4 className="text-xl font-bold text-foreground mb-2">Message Sent!</h4>
                    <p className="text-muted-foreground">We'll get back to you soon.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Your name"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+91 XXXXX XXXXX"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subject">Subject</Label>
                      <Input
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="Product inquiry, Order status, etc."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us how we can help you..."
                        rows={5}
                        required
                      />
                    </div>
                    <Button type="submit" variant="default" size="lg" className="w-full" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>Sending...</>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Quick Links */}
      <section className="py-16 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="section-title text-foreground">Common Questions</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Card className="bg-card border-0 shadow-soft hover:shadow-elevated transition-all">
              <CardContent className="p-6 text-center">
                <h4 className="font-bold text-foreground mb-2">How to verify authenticity?</h4>
                <p className="text-sm text-muted-foreground">All products come with authenticity codes you can verify on brand websites.</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-0 shadow-soft hover:shadow-elevated transition-all">
              <CardContent className="p-6 text-center">
                <h4 className="font-bold text-foreground mb-2">Delivery to Nepal?</h4>
                <p className="text-sm text-muted-foreground">Yes! We ship to Nepal. Contact us for delivery timeline and charges.</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-0 shadow-soft hover:shadow-elevated transition-all">
              <CardContent className="p-6 text-center">
                <h4 className="font-bold text-foreground mb-2">Payment methods?</h4>
                <p className="text-sm text-muted-foreground">We only accept prepaid orders via QR payment or bank transfer. No COD available.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;
